import { Text, Button, PageHeader, tokens } from '@hotjar/hotsauce';
import { getSurveyExternalUrl } from '@hotjar/common';
import { navigateTo } from '@hotjar/webapp/utils/routing';
import { SURVEYS, SURVEY_TEMPLATES } from '@hotjar/webapp/constants/routes';
import { Page } from '@hotjar/webapp/components/Page';
import styled from '@emotion/styled';
import { useCallback, useState } from 'react';
import { useNotify } from '@hotjar/webapp/hooks/useNotify';
import { useCurrentSite } from '@hotjar/site';
import { useTranslation } from 'react-i18next';
import { useRenameSurveyMutation, useSurveyWidgetQuery } from '@hotjar/feedback';
import { usePermission } from '@hotjar/webapp/domains/authorization';
import { SharingModule } from '@hotjar/webapp/components/SharingModule';
import { EntityActionsDropdown } from '../../../common/EntityActions/EntityActionsDropdown';
import { EntityCreator } from '../../../common/EntityCreator';
import { EntityRenameModal } from '../../../common/EntityActions/EntityRenameModal';
import { isOffsite } from '../../../../domain/Surveys/Survey/SurveyLocations';
import { SurveyExternalLink } from './SurveyExternalLink';
import { SurveyActivationSwitch } from './SurveyActivationSwitch';
import { useDeleteSurvey } from '../../hooks/useDeleteSurvey';

const StyledPage = styled(Page)`
  padding: ${tokens.spacer.x3} ${tokens.spacer.x5} 0;
  margin-bottom: ${tokens.spacer.x3};
`;

export interface SurveyDetailsHeaderProps {
  widgetId: number | undefined;
  hideSharingModule?: boolean;
}

export function SurveyDetailsHeader({
  widgetId,
  hideSharingModule,
}: SurveyDetailsHeaderProps): JSX.Element {
  const { data: currentSite } = useCurrentSite();
  const { data: widget } = useSurveyWidgetQuery(currentSite?.id, widgetId);
  const { success, error } = useNotify();
  const onDeleteSuccess = useCallback(() => {
    if (currentSite !== undefined) {
      navigateTo(SURVEYS.RESPONSES.path, { siteId: currentSite.id });
    }
  }, [currentSite]);
  const { beginDeleteSurvey, deleteSurveyModal } = useDeleteSurvey(
    widget?.id,
    widget?.name,
    onDeleteSuccess,
  );

  const navigateToSurveyEdit = useCallback(() => {
    if (!widget || !currentSite) return;
    navigateTo(SURVEYS.EDIT.path, { siteId: currentSite.id, surveyId: widget.id });
  }, [widget, currentSite]);

  const navigateToSurveyDuplicate = useCallback(() => {
    if (!widget || !currentSite) return;
    navigateTo(SURVEYS.CREATE.path, { siteId: currentSite.id, copyId: widget.id });
  }, [widget, currentSite]);

  const navigateToInAppTemplates = useCallback(() => {
    if (!currentSite) return;
    navigateTo(SURVEY_TEMPLATES.BASE.path, { siteId: currentSite.id });
  }, [currentSite]);

  const hasSurveyUpdatePermission = usePermission('surveys-update');
  const hasSurveyCreatePermission = usePermission('surveys-create');
  const hasSurveyDeletePermission = usePermission('surveys-delete');

  const [isRenameModalOpen, setRenameModalOpen] = useState(false);
  const [isRenamePending, setRenamePending] = useState(false);
  const renameSurveyMutation = useRenameSurveyMutation();
  const { t } = useTranslation('feedback');
  const onRename = useCallback(
    async (name: string) => {
      if (!widget || !currentSite) return;

      setRenamePending(true);
      try {
        await renameSurveyMutation.mutateAsync({
          siteId: currentSite.id,
          surveyId: widget.id,
          name,
        });
      } catch {
        error(t('sidebar.widgets.rename-error-survey'));
        return;
      } finally {
        setRenamePending(false);
      }
      setRenameModalOpen(false);
      success(t('sidebar.widgets.renamed-survey'));
    },
    [widget, currentSite, renameSurveyMutation, success, error, t],
  );

  return (
    <StyledPage>
      <PageHeader
        title={widget?.name ?? 'Surveys'}
        actions={
          <>
            {widget && currentSite && (
              <>
                <EntityCreator
                  createdBy={widget.created_by_user}
                  createdTime={widget.created_epoch_time}
                />

                <SurveyActivationSwitch
                  isEnabledInitalState={widget.enabled}
                  siteId={currentSite.id}
                  surveyId={widget.id}
                  hasPermission={hasSurveyUpdatePermission}
                />
                <EntityActionsDropdown
                  onDelete={hasSurveyDeletePermission ? beginDeleteSurvey : undefined}
                  onEdit={hasSurveyUpdatePermission ? navigateToSurveyEdit : undefined}
                  onDuplicate={hasSurveyCreatePermission ? navigateToSurveyDuplicate : undefined}
                  onRename={hasSurveyUpdatePermission ? () => setRenameModalOpen(true) : undefined}
                />
              </>
            )}
            {!hideSharingModule ? (
              <SharingModule
                trigger={
                  <Button size="s" kind="secondary">
                    {t('common:actions.share')}
                  </Button>
                }
              />
            ) : null}

            <Button
              kind="primary"
              size="s"
              icon="plus"
              isDisabled={!hasSurveyCreatePermission}
              onClick={navigateToInAppTemplates}
            >
              {t('survey-responses.header.action.new-survey')}
            </Button>
          </>
        }
        subtitle={
          widget !== undefined ? (
            <>
              {widget.description !== null && <Text textType="body1">{widget.description}</Text>}
              {isOffsite(widget.display_type) && (
                <SurveyExternalLink surveyExternalLink={getSurveyExternalUrl(widget.uuid)} />
              )}
            </>
          ) : (
            <Text textType="body1" textColor="text_light">
              {t('empty-states.not-found.subtitle')}
            </Text>
          )
        }
      />
      {deleteSurveyModal}
      {widget && isRenameModalOpen && (
        <EntityRenameModal
          entityName={widget.name}
          entityType="survey"
          isRenamePending={isRenamePending}
          isOpen={isRenameModalOpen}
          onRename={onRename}
          onClose={() => setRenameModalOpen(false)}
        />
      )}
    </StyledPage>
  );
}
